import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const CurrencySelector = ({ variant = 'default', onSelect }) => {
  const { selectedCurrency, setSelectedCurrency, currencies, currency } = useCurrency();
  const [isOpen, setIsOpen] = useState(variant === 'inline');
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    if (onSelect) {
      onSelect(code);
    } else {
      setSelectedCurrency(code);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  // Filter currencies based on search term
  const filteredCurrencies = currencies.filter(curr => 
    curr.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    curr.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Popular currencies to show at top
  const popularCodes = ['GBP', 'USD', 'EUR', 'SAR', 'AED', 'PKR', 'INR', 'BDT', 'MYR', 'TRY'];
  const popularCurrencies = popularCodes
    .map(code => currencies.find(c => c.code === code))
    .filter(Boolean);

  // Inline variant - shows a grid of currencies
  if (variant === 'inline') {
    return (
      <div 
        className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden animate-fadeIn"
        data-testid="currency-inline-selector"
      >
        {/* Search */}
        <div className="p-3 bg-gray-50 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search currencies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              data-testid="currency-search-input"
            />
          </div>
        </div>

        {/* Popular currencies when not searching */}
        {!searchTerm && (
          <div className="p-3 border-b bg-gradient-to-r from-amber-50 to-amber-50">
            <div className="text-xs font-semibold text-gray-700 mb-2">POPULAR CURRENCIES</div>
            <div className="grid grid-cols-5 gap-2">
              {popularCurrencies.map((curr) => (
                <button
                  key={curr.code}
                  onClick={() => handleSelect(curr.code)}
                  data-testid={`popular-currency-${curr.code}`}
                  className={`flex flex-col items-center p-2 rounded-lg transition-all hover:bg-white hover:shadow ${
                    selectedCurrency === curr.code 
                      ? 'bg-white shadow ring-2 ring-amber-500' 
                      : 'bg-white/50'
                  }`}
                >
                  <span className="text-xl mb-1">{curr.flag}</span>
                  <span className="text-xs font-semibold text-gray-700">{curr.code}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* All currencies list */}
        <div className="max-h-48 overflow-y-auto">
          {!searchTerm && (
            <div className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
              ALL CURRENCIES ({currencies.length})
            </div>
          )}
          {filteredCurrencies.map((curr) => (
            <button
              key={curr.code}
              onClick={() => handleSelect(curr.code)}
              data-testid={`currency-inline-${curr.code}`}
              className={`w-full px-4 py-2 flex items-center gap-3 hover:bg-amber-50 transition-colors text-left border-b border-gray-100 last:border-0 ${
                selectedCurrency === curr.code ? 'bg-amber-50' : ''
              }`}
            >
              <span className="text-lg">{curr.flag}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">{curr.code}</span>
                  <span className="text-gray-400 text-sm">{curr.symbol}</span>
                </div>
                <div className="text-xs text-gray-500 truncate">{curr.name}</div>
              </div>
              {selectedCurrency === curr.code && (
                <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
              )}
            </button>
          ))}
          {filteredCurrencies.length === 0 && (
            <div className="p-4 text-center text-gray-500 text-sm">
              No currencies found for "{searchTerm}"
            </div>
          )}
        </div>
      </div>
    );
  }

  // Compact variant (for header)
  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          data-testid="currency-selector-btn"
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-md text-white text-sm font-medium transition-colors border border-gray-600"
        >
          <span className="text-base">{currency.flag}</span>
          <span>{currency.code}</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div 
            className="absolute left-0 mt-2 w-72 bg-white rounded-xl shadow-2xl overflow-hidden"
            style={{ zIndex: 9999 }}
            data-testid="currency-dropdown"
          >
            {/* Search */}
            <div className="p-3 bg-gradient-to-r from-amber-600 to-amber-600">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-1.5 text-sm bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {filteredCurrencies.map((curr) => (
                <button
                  key={curr.code}
                  onClick={() => handleSelect(curr.code)}
                  data-testid={`currency-option-${curr.code}`}
                  className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-amber-50 transition-colors text-left border-b border-gray-100 last:border-0 ${
                    selectedCurrency === curr.code ? 'bg-amber-50' : ''
                  }`}
                >
                  <span className="text-xl">{curr.flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800">{curr.code}</div>
                    <div className="text-xs text-gray-500 truncate">{curr.name}</div>
                  </div>
                  {selectedCurrency === curr.code && (
                    <Check className="w-5 h-5 text-amber-600" />
                  )}
                </button>
              ))}
              {filteredCurrencies.length === 0 && (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No currencies found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="currency-selector-btn-default"
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-amber-500 transition-colors shadow-sm"
      >
        <span className="text-lg">{currency.flag}</span>
        <span className="font-medium text-gray-800">{currency.code}</span>
        <span className="text-gray-500">({currency.symbol})</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl overflow-hidden"
          style={{ zIndex: 9999 }}
          data-testid="currency-dropdown-default"
        >
          {/* Search */}
          <div className="p-3 bg-gradient-to-r from-amber-600 to-amber-600">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
              <input
                type="text"
                placeholder="Search currencies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
          </div>
          
          <div className="max-h-72 overflow-y-auto">
            {filteredCurrencies.map((curr) => (
              <button
                key={curr.code}
                onClick={() => handleSelect(curr.code)}
                data-testid={`currency-option-default-${curr.code}`}
                className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-amber-50 transition-colors text-left border-b border-gray-100 last:border-0 ${
                  selectedCurrency === curr.code ? 'bg-amber-50' : ''
                }`}
              >
                <span className="text-2xl">{curr.flag}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-800">{curr.code}</span>
                    <span className="text-gray-500">{curr.symbol}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{curr.name}</div>
                </div>
                {selectedCurrency === curr.code && (
                  <Check className="w-5 h-5 text-amber-600" />
                )}
              </button>
            ))}
            {filteredCurrencies.length === 0 && (
              <div className="p-4 text-center text-gray-500 text-sm">
                No currencies found for "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;
